/**
 * Split.io treatment caching utilities.
 *
 * This module handles localStorage caching for feature flag treatments
 * to prevent UI flickering on subsequent page loads.
 *
 * IMPORTANT - Optimistic Caching Approach:
 * This cache uses an optimistic strategy: if a treatment exists in localStorage,
 * it's used immediately without waiting for user data verification. This avoids
 * UI flickering but means the cached value may not reflect the user's current
 * eligibility. Only use this for UI-related flags where preventing flicker is
 * more important than strict real-time eligibility verification.
 *
 * Key behaviors:
 * - Only flags in CACHEABLE_SPLITS are cached automatically (prevents localStorage bloat)
 * - Manual overrides (plain strings) always take priority over cached values
 * - Cache expires after 10 days of inactivity (sliding TTL)
 * - Fresh cache (< 30 seconds) takes priority in useSplit hook to bridge Split.io CDN propagation delays
 * - Explicit calls with force=true bypass the allowlist (for user toggles)
 */

export const SPLIT_STORAGE_PREFIX = 'split.io/'
const CACHE_TTL_MS = 10 * 24 * 60 * 60 * 1000 // 10 days in milliseconds

/**
 * Feature flags that should be cached automatically in localStorage.
 * Only flags in this list will have their treatments cached to prevent flickering.
 *
 * Criteria for inclusion:
 * - Flag affects visual UI rendering during page load
 * - Prevents noticeable flashing/flickering of UI elements
 *
 * Note: Manual overrides (plain strings) still work for any flag.
 * Note: Explicit setCachedTreatment(name, value, config, force=true) bypasses this list.
 */
const CACHEABLE_SPLITS: ReadonlySet<string> = new Set([])

export type CachedTreatment =
  | { type: 'override'; treatment: string }
  | { type: 'cache'; treatment: SplitIO.TreatmentWithConfig; expired: boolean }

interface StoredCache {
  treatment: string
  config: string | null
  cachedAt: number
}

function isStoredCache(value: unknown): value is StoredCache {
  return (
    typeof value === 'object' &&
    value !== null &&
    'treatment' in value &&
    typeof (value as StoredCache).treatment === 'string'
  )
}

/**
 * Check if a localStorage value is a manual override (plain string, not JSON)
 */
function isManualOverride(value: string): boolean {
  try {
    JSON.parse(value)
    return false
  } catch {
    return true
  }
}

/**
 * Read cached treatment from localStorage (pure function, no side effects)
 */
export function getCachedTreatment(name: string): CachedTreatment | null {
  try {
    const storageKey = `${SPLIT_STORAGE_PREFIX}${name}`
    const cached = localStorage.getItem(storageKey)
    if (!cached) return null

    // Try to parse as JSON (cached treatment with config)
    try {
      const parsed: unknown = JSON.parse(cached)
      if (isStoredCache(parsed)) {
        // Check if cache has expired (older than 10 days)
        const cachedAt = parsed.cachedAt || 0
        const expired = Date.now() - cachedAt > CACHE_TTL_MS

        return {
          type: 'cache',
          treatment: { treatment: parsed.treatment, config: parsed.config },
          expired,
        }
      }
    } catch {
      // Not JSON - treat as manual override (plain string)
    }

    // Plain string = manual override (works for any flag)
    return { type: 'override', treatment: cached }
  } catch {
    return null
  }
}

/**
 * Refresh the TTL for a cached treatment
 */
export function refreshCacheTTL(name: string): void {
  try {
    const storageKey = `${SPLIT_STORAGE_PREFIX}${name}`
    const cached = localStorage.getItem(storageKey)
    if (!cached) return

    const parsed: unknown = JSON.parse(cached)
    if (isStoredCache(parsed)) {
      const refreshed: StoredCache = { ...parsed, cachedAt: Date.now() }
      localStorage.setItem(storageKey, JSON.stringify(refreshed))
    }
  } catch {
    // Ignore errors
  }
}

/**
 * Remove a cached treatment from localStorage
 */
export function clearCachedTreatment(name: string): void {
  try {
    localStorage.removeItem(`${SPLIT_STORAGE_PREFIX}${name}`)
  } catch {
    // Ignore errors
  }
}

/**
 * Cache a treatment value in localStorage
 *
 * @param name - Feature flag name
 * @param treatmentValue - Treatment value to cache ('on', 'off', etc.)
 * @param configValue - Optional config JSON string
 * @param force - If true, bypasses CACHEABLE_SPLITS allowlist (for explicit user actions)
 */
export function setCachedTreatment(
  name: string,
  treatmentValue: string,
  configValue: string | null,
  force = false,
): void {
  try {
    // Only cache valid treatments (not 'control' which is the default)
    // Don't overwrite manual overrides (plain strings)
    if (treatmentValue !== 'control') {
      // Check allowlist unless force=true
      if (!force && !CACHEABLE_SPLITS.has(name)) {
        return
      }

      const storageKey = `${SPLIT_STORAGE_PREFIX}${name}`
      const existing = localStorage.getItem(storageKey)
      // Don't overwrite manual overrides
      if (existing && isManualOverride(existing)) {
        return
      }

      const toStore: StoredCache = {
        treatment: treatmentValue,
        config: configValue,
        cachedAt: Date.now(),
      }
      localStorage.setItem(storageKey, JSON.stringify(toStore))
    }
  } catch {
    // Ignore localStorage errors
  }
}
